import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  CreditCard, Banknote, Split, X, Check, Loader2, 
  Calculator, Receipt, Mail, WifiOff, Printer
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { printReceipt as printReceiptUtil } from '../../../utils/printing';

export default function PaymentModal({ 
  open, 
  onClose, 
  order, 
  subtotal, 
  restaurantId,
  onComplete,
  isOffline = false,
  onOfflinePayment
}) {
  const { api } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(true);
  const [emailReceipt, setEmailReceipt] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');

  // Calculate totals
  const tip = parseFloat(tipAmount) || 0;
  const total = subtotal + tip;
  const cashReceivedNum = parseFloat(cashReceived) || 0;
  const change = cashReceivedNum - total;

  // Quick cash buttons
  const quickCashAmounts = [5, 10, 20, 50];

  const handleQuickCash = (amount) => {
    setCashReceived(amount.toString());
  };

  // Handle offline cash payment
  const handleOfflineCashPayment = () => {
    if (cashReceivedNum < total) {
      toast.error('Insufficient cash amount');
      return;
    }
    
    if (onOfflinePayment) {
      onOfflinePayment('cash');
      onClose();
    }
  };

  const handleCashPayment = async () => {
    if (cashReceivedNum < total) {
      toast.error('Insufficient cash amount');
      return;
    }

    // If offline, use offline handler
    if (isOffline) {
      handleOfflineCashPayment();
      return;
    }

    setProcessing(true);
    try {
      const orderData = {
        restaurant_id: restaurantId,
        items: order.items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: item.notes
        })),
        order_type: 'dine_in',
        status: 'completed',
        payment_method: 'cash',
        payment_status: 'paid',
        notes: order.notes,
        customer_name: order.customer_name || 'Walk-in',
        table_number: order.table_number,
        subtotal: subtotal,
        tip_amount: tip,
        total: total,
        cash_received: cashReceivedNum,
        change_given: change
      };

      const response = await api.post('/orders', orderData);
      
      // Print receipt if enabled
      if (printReceipt) {
        const completedOrder = {
          ...orderData,
          id: response.data.order_id || response.data.id,
          created_at: new Date().toISOString()
        };
        // Get restaurant info for receipt
        try {
          const restaurantRes = await api.get(`/restaurants/${restaurantId}`);
          printReceiptUtil(completedOrder, restaurantRes.data);
        } catch (e) {
          printReceiptUtil(completedOrder, { name: 'Restaurant' });
        }
      }

      onComplete({ 
        method: 'cash', 
        orderId: response.data.id,
        change: change
      });
      resetForm();
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCardPayment = async () => {
    setProcessing(true);
    try {
      const orderData = {
        restaurant_id: restaurantId,
        items: order.items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: item.notes
        })),
        order_type: 'dine_in',
        status: 'completed',
        payment_method: 'card',
        payment_status: 'paid',
        notes: order.notes,
        customer_name: order.customer_name || 'Walk-in',
        table_number: order.table_number,
        subtotal: subtotal,
        tip_amount: tip,
        total: total
      };

      const response = await api.post('/orders', orderData);

      // Print receipt if enabled
      if (printReceipt) {
        const completedOrder = {
          ...orderData,
          id: response.data.order_id || response.data.id,
          created_at: new Date().toISOString()
        };
        try {
          const restaurantRes = await api.get(`/restaurants/${restaurantId}`);
          printReceiptUtil(completedOrder, restaurantRes.data);
        } catch (e) {
          printReceiptUtil(completedOrder, { name: 'Restaurant' });
        }
      }

      onComplete({ 
        method: 'card', 
        orderId: response.data.id 
      });
      resetForm();
    } catch (error) {
      console.error('Card payment failed:', error);
      toast.error('Card payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setCashReceived('');
    setTipAmount('');
    setPaymentMethod('cash');
    setPrintReceipt(true);
    setEmailReceipt(false);
    setCustomerEmail('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Process Payment
            {isOffline && (
              <Badge variant="destructive" className="ml-2 animate-pulse">
                <WifiOff className="w-3 h-3 mr-1" /> Offline
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Offline Warning */}
        {isOffline && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
              <WifiOff className="w-4 h-4" />
              Offline Mode - Only cash payments available
            </p>
            <p className="text-amber-600 text-xs mt-1">
              Order will be saved locally and synced when back online
            </p>
          </div>
        )}

        {/* Order Summary */}
        <div className="bg-slate-50 rounded-lg p-4 mb-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Subtotal</span>
              <span>£{subtotal.toFixed(2)}</span>
            </div>
            {tip > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Tip</span>
                <span>£{tip.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
              <span>Total</span>
              <span className="text-orange-500">£{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Tip Input */}
        <div className="mb-4">
          <Label>Add Tip (optional)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              className="flex-1"
              data-testid="tip-input"
            />
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setTipAmount((subtotal * 0.1).toFixed(2))}
            >
              10%
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setTipAmount((subtotal * 0.15).toFixed(2))}
            >
              15%
            </Button>
          </div>
        </div>

        {/* Payment Method Tabs */}
        <Tabs value={paymentMethod} onValueChange={(v) => !isOffline || v === 'cash' ? setPaymentMethod(v) : null}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="cash" className="flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              Cash
            </TabsTrigger>
            <TabsTrigger 
              value="card" 
              className="flex items-center gap-2"
              disabled={isOffline}
            >
              <CreditCard className="w-4 h-4" />
              Card
              {isOffline && <WifiOff className="w-3 h-3 text-red-400" />}
            </TabsTrigger>
          </TabsList>

          {/* Cash Payment */}
          <TabsContent value="cash" className="space-y-4">
            <div>
              <Label>Cash Received</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter amount"
                value={cashReceived}
                onChange={(e) => setCashReceived(e.target.value)}
                className="text-lg font-semibold h-12 mt-1"
                data-testid="cash-received-input"
              />
            </div>

            {/* Quick Cash Buttons */}
            <div className="grid grid-cols-4 gap-2">
              {quickCashAmounts.map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  onClick={() => handleQuickCash(amount)}
                  data-testid={`quick-cash-${amount}`}
                >
                  £{amount}
                </Button>
              ))}
            </div>

            {/* Exact Amount Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setCashReceived(total.toFixed(2))}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Exact Amount (£{total.toFixed(2)})
            </Button>

            {/* Change Display */}
            {cashReceivedNum > 0 && (
              <div className={`p-4 rounded-lg text-center ${
                change >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                <div className="text-sm">Change Due</div>
                <div className="text-3xl font-bold" data-testid="change-amount">
                  £{change >= 0 ? change.toFixed(2) : '0.00'}
                </div>
                {change < 0 && (
                  <div className="text-sm mt-1">
                    Still owed: £{Math.abs(change).toFixed(2)}
                  </div>
                )}
              </div>
            )}

            <Button
              className="w-full h-12 bg-green-600 hover:bg-green-700"
              onClick={handleCashPayment}
              disabled={processing || cashReceivedNum < total}
              data-testid="complete-cash-payment"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Complete Cash Payment
            </Button>
          </TabsContent>

          {/* Card Payment */}
          <TabsContent value="card" className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <CreditCard className="w-12 h-12 text-blue-500 mx-auto mb-2" />
              <p className="text-blue-700">
                Ready to process card payment
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Amount: <span className="font-bold">£{total.toFixed(2)}</span>
              </p>
            </div>

            <Button
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              onClick={handleCardPayment}
              disabled={processing}
              data-testid="complete-card-payment"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Process Card Payment
            </Button>
          </TabsContent>
        </Tabs>

        {/* Receipt Options */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 cursor-pointer">
              <Checkbox 
                checked={printReceipt} 
                onCheckedChange={setPrintReceipt}
                data-testid="print-receipt-checkbox"
              />
              <Receipt className="w-4 h-4" />
              Print Receipt
            </Label>
          </div>
          
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 cursor-pointer">
              <Checkbox 
                checked={emailReceipt} 
                onCheckedChange={setEmailReceipt}
                data-testid="email-receipt-checkbox"
              />
              <Mail className="w-4 h-4" />
              Email Receipt
            </Label>
          </div>

          {emailReceipt && (
            <Input
              type="email"
              placeholder="customer@email.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              data-testid="customer-email-input"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
